import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    MenuItem,
    Grid,
    Button
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchAllTransactions, fetchAllSellers } from "../../../State/admin/adminSlice";

const AdminTransactionsTable = () => {
    const dispatch = useAppDispatch();
    const { transactions, sellers, loading } = useAppSelector((store) => store.admin);

    const [searchTransactionId, setSearchTransactionId] = useState("");
    const [selectedSeller, setSelectedSeller] = useState("ALL");

    // Helper to format date as YYYY-MM-DD
    const formatDateString = (date: Date) => date.toISOString().split("T")[0];

    // Default to the last 24 hours (Today)
    const todayStr = formatDateString(new Date());

    const [fromDate, setFromDate] = useState(todayStr);
    const [toDate, setToDate] = useState(todayStr);

    // Applied filter states default to showing the past 24 hours/today
    const [appliedFilters, setAppliedFilters] = useState({
        searchId: "",
        seller: "ALL",
        from: todayStr,
        to: todayStr
    });

    useEffect(() => {
        dispatch(fetchAllTransactions());
        dispatch(fetchAllSellers(localStorage.getItem("jwt") || ""));
    }, [dispatch]);

    // Handle filter action
    const handleApplyFilter = () => {
        setAppliedFilters({
            searchId: searchTransactionId,
            seller: selectedSeller,
            from: fromDate,
            to: toDate
        });
    };

    // Handle reset action (Clears all filters, showing all transactions)
    const handleResetFilter = () => {
        setSearchTransactionId("");
        setSelectedSeller("ALL");
        setFromDate("");
        setToDate("");
        setAppliedFilters({
            searchId: "",
            seller: "ALL",
            from: "",
            to: ""
        });
    };

    // Filter logic based on applied criteria
    const filteredTransactions = transactions.filter((tx) => {
        const txIdString = String(
            tx.order?.paymentDetails?.mpesaReceiptNumber ||
            tx.order?.paymentDetails?.paymentId ||
            ""
        ).toLowerCase();
        const matchesId = txIdString.includes(appliedFilters.searchId.toLowerCase());

        const matchesSeller = appliedFilters.seller === "ALL" || tx.seller?.id?.toString() === appliedFilters.seller;

        // Robust Date Range filtering logic
        let matchesDate = true;
        if (tx.date) {
            let txDateObj: Date;

            if (Array.isArray(tx.date)) {
                const [y, m, d, h = 0, min = 0, s = 0] = tx.date;
                txDateObj = new Date(y, m - 1, d, h, min, s);
            } else {
                txDateObj = new Date(tx.date);
            }

            if (!isNaN(txDateObj.getTime())) {
                const txDateOnly = formatDateString(txDateObj);

                if (appliedFilters.from && txDateOnly < appliedFilters.from) {
                    matchesDate = false;
                }
                if (appliedFilters.to && txDateOnly > appliedFilters.to) {
                    matchesDate = false;
                }
            }
        } else if (appliedFilters.from || appliedFilters.to) {
            matchesDate = false;
        }

        return matchesId && matchesSeller && matchesDate;
    });

    // Calculate total amount sum for the filtered list safely
    const totalFilteredAmount = filteredTransactions.reduce((sum, tx) => {
        const rawAmount = tx.amount ?? tx.order?.totalSellingPrice ?? 0;
        const numericAmount = typeof rawAmount === "number" ? rawAmount : parseFloat(rawAmount) || 0;
        return sum + numericAmount;
    }, 0);

    // Export filtered transactions to CSV
    const handleExportCSV = () => {
        if (filteredTransactions.length === 0) {
            alert("No transactions available to export.");
            return;
        }

        const headers = ["Date", "Transaction ID", "Order ID", "Seller", "Customer", "Payment Status", "Amount"];
        const rows = filteredTransactions.map((tx) => [
            `"${tx.date ? new Date(Array.isArray(tx.date) ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2], tx.date[3] || 0, tx.date[4] || 0) : tx.date).toLocaleString() : "N/A"}"`,
            `"${tx.order?.paymentDetails?.mpesaReceiptNumber ||
            tx.order?.paymentDetails?.paymentId || ""}"`,
            `"${tx.order?.id ? `#${tx.order.id}` : "N/A"}"`,
            `"${tx.seller?.businessDetails?.businessName || tx.seller?.sellerName || "N/A"}"`,
            `"${tx.customer?.email || tx.customer?.name || "N/A"}"`,
            `"${tx.order?.paymentDetails?.paymentStatus || tx.order?.paymentStatus || "N/A"}"`,
            `"${tx.amount || 0}"`
        ]);

        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `transactions_export_${formatDateString(new Date())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box className="p-5">
            <div className="flex justify-between items-center pb-4">
                <Typography variant="h4" className="font-bold">
                    All Transactions (Last 24 Hours Default)
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCSV}
                    size="small"
                >
                    Export to CSV
                </Button>
            </div>

            {/* Filter and Search Controls */}
            <Paper className="p-4 mb-5 shadow-sm">
                <Grid container spacing={3} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            label="Search by Transaction ID"
                            variant="outlined"
                            size="small"
                            value={searchTransactionId}
                            onChange={(e) => setSearchTransactionId(e.target.value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            select
                            label="Filter by Seller"
                            variant="outlined"
                            size="small"
                            value={selectedSeller}
                            onChange={(e) => setSelectedSeller(e.target.value)}
                        >
                            <MenuItem value="ALL">All Sellers</MenuItem>
                            {sellers.map((seller) => (
                                <MenuItem key={seller.id} value={seller.id.toString()}>
                                    {seller.businessDetails?.businessName || seller.sellerName || seller.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="From Date"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="To Date"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 2 }} className="flex gap-2">
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            fullWidth
                            onClick={handleApplyFilter}
                        >
                            Filter
                        </Button>
                        <Button
                            variant="outlined"
                            color="inherit"
                            size="medium"
                            fullWidth
                            onClick={handleResetFilter}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Summary Total Bar */}
            <Paper className="p-3 mb-4 bg-gray-50 flex justify-between items-center px-4">
                <Typography variant="subtitle1" className="font-semibold">
                    Filtered Count: {filteredTransactions.length} transaction(s)
                </Typography>
                <Typography variant="subtitle1" className="font-bold text-primary">
                    Total Amount (Filtered): {totalFilteredAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
            </Paper>

            {/* Table implementation */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Transaction ID</b></TableCell>
                            <TableCell><b>Order ID</b></TableCell>
                            <TableCell><b>Seller</b></TableCell>
                            <TableCell><b>Customer</b></TableCell>
                            <TableCell><b>Payment Status</b></TableCell>
                            <TableCell align="right"><b>Amount</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTransactions.length > 0 ? (
                            filteredTransactions.map((tx) => (
                                <TableRow key={tx.id}>
                                    <TableCell>
                                        {tx.date
                                            ? (Array.isArray(tx.date)
                                                ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2], tx.date[3] || 0, tx.date[4] || 0).toLocaleString()
                                                : new Date(tx.date).toLocaleString())
                                            : "N/A"}
                                    </TableCell>
                                    <TableCell>
                                        {tx.order?.paymentDetails?.mpesaReceiptNumber ||
                                            tx.order?.paymentDetails?.paymentId ||
                                            "N/A"}
                                    </TableCell>
                                    <TableCell>{tx.order?.id ? `#${tx.order.id}` : "N/A"}</TableCell>
                                    <TableCell>{tx.seller?.businessDetails?.businessName || tx.seller?.sellerName || "N/A"}</TableCell>
                                    <TableCell>{tx.customer?.email || tx.customer?.name || "N/A"}</TableCell>
                                    <TableCell>
                                        {tx.order?.paymentDetails?.paymentStatus || tx.order?.paymentStatus || "N/A"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {(() => {
                                            const amt = tx.amount ?? tx.order?.totalSellingPrice ?? 0;
                                            return amt.toLocaleString(undefined, { minimumFractionDigits: 2 });
                                        })()}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {loading ? "Loading transactions..." : "No transactions found for the selected criteria"}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default AdminTransactionsTable;